/**
 * LensOrb.jsx — GRANULLO_LENS_ORGANISM_V1
 *
 * The cognitive optic traveling through the proof rail nervous system.
 * Cinematic motion. Section-aware. Scarlet proof accents.
 * Shares animation language with EditorialSpine proof rail.
 *
 * Uses native pointer events for drag (no Framer Motion drag).
 * Spring values control position with premium physics.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { useGranulloLensV2 } from './GranulloLensV2Provider';
import { parseLensAnnotation, LENS_ATTRS } from './LensAnnotations';

const MAGNETIC_RANGE = 80;
const ORB_SIZE = 48;
const SCARLET = '#CD2200';

export function LensOrb() {
  const { inspectElement, focusedAnnotation } = useGranulloLensV2();
  const [isDragging, setIsDragging] = useState(false);
  const [isPickedUp, setIsPickedUp] = useState(false);
  const [nearTarget, setNearTarget] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [restPosition, setRestPosition] = useState({ x: 0, y: 0 });
  const [sectionLabel, setSectionLabel] = useState('');
  const [sectionPulse, setSectionPulse] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('granullo_lens_hint_seen') === 'true';
    }
    return false;
  });
  const [dropMissFlash, setDropMissFlash] = useState(false);
  const orbRef = useRef(null);
  const dragStartRef = useRef(null);
  const nearTargetRef = useRef(null);
  const lastSectionRef = useRef('');

  // Premium spring physics — snappy during drag, smooth on release
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 700, damping: 48, mass: 0.25, restDelta: 0.0005 });
  const springY = useSpring(y, { stiffness: 700, damping: 48, mass: 0.25, restDelta: 0.0005 });

  // Synchronize with Proof Rail
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (detail?.activeLabel && detail.activeLabel !== lastSectionRef.current) {
        lastSectionRef.current = detail.activeLabel;
        setSectionLabel(detail.activeLabel);
        // Section change pulse
        setSectionPulse(true);
        setTimeout(() => setSectionPulse(false), 800);
      }
    };
    window.addEventListener('granullo-proof-rail-update', handler);
    return () => window.removeEventListener('granullo-proof-rail-update', handler);
  }, []);

  // Listen for corridor focus events — geographic cognition
  const [corridorNarrative, setCorridorNarrative] = useState('');
  useEffect(() => {
    const corridorHandler = (e) => {
      if (e.detail?.narrative) {
        setCorridorNarrative(e.detail.narrative);
        setSectionPulse(true);
        setTimeout(() => setSectionPulse(false), 600);
      }
    };
    const corridorClear = () => setCorridorNarrative('');
    window.addEventListener('granullo-corridor-focus', corridorHandler);
    window.addEventListener('granullo-corridor-blur', corridorClear);
    return () => {
      window.removeEventListener('granullo-corridor-focus', corridorHandler);
      window.removeEventListener('granullo-corridor-blur', corridorClear);
    };
  }, []);

  const findAnnotatedElements = useCallback(() => {
    return Array.from(document.querySelectorAll(`[${LENS_ATTRS.ID}]`));
  }, []);

  const checkMagneticProximity = useCallback((clientX, clientY) => {
    const annotated = findAnnotatedElements();
    let closest = null;
    let closestDist = MAGNETIC_RANGE;

    for (const el of annotated) {
      const rect = el.getBoundingClientRect();
      const dx = Math.max(rect.left - clientX, 0, clientX - rect.right);
      const dy = Math.max(rect.top - clientY, 0, clientY - rect.bottom);
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < closestDist) {
        closestDist = dist;
        closest = el;
      }
    }

    if (nearTargetRef.current && nearTargetRef.current !== closest) {
      nearTargetRef.current.style.boxShadow = '';
    }

    if (closest) {
      nearTargetRef.current = closest;
      setNearTarget(closest);
      closest.style.boxShadow = `0 0 0 2px ${SCARLET}18, 0 0 16px ${SCARLET}0A`;
      closest.style.transition = 'box-shadow 200ms ease';
    } else {
      if (nearTargetRef.current) nearTargetRef.current.style.boxShadow = '';
      nearTargetRef.current = null;
      setNearTarget(null);
    }
  }, [findAnnotatedElements]);

  const clearMagneticGlow = useCallback(() => {
    if (nearTargetRef.current) nearTargetRef.current.style.boxShadow = '';
    nearTargetRef.current = null;
    setNearTarget(null);
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    dragStartRef.current = { x: e.clientX, y: e.clientY, startX: x.get(), startY: y.get(), moved: false };
    setIsPickedUp(true);

    const handlePointerMove = (moveEvt) => {
      if (!dragStartRef.current) return;
      const dx = moveEvt.clientX - dragStartRef.current.x;
      const dy = moveEvt.clientY - dragStartRef.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        dragStartRef.current.moved = true;
        setIsDragging(true);
      }
      x.set(dragStartRef.current.startX + dx);
      y.set(dragStartRef.current.startY + dy);
      checkMagneticProximity(moveEvt.clientX, moveEvt.clientY);
    };

    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      setIsPickedUp(false);
      setIsDragging(false);

      const currentNearTarget = nearTargetRef.current;

      if (dragStartRef.current?.moved && currentNearTarget) {
        const annotation = parseLensAnnotation(currentNearTarget);
        if (annotation) inspectElement(currentNearTarget);
        clearMagneticGlow();
        x.set(restPosition.x);
        y.set(restPosition.y);
      } else if (dragStartRef.current?.moved) {
        // Dropped on an un-annotated area — flash a "miss" hint so user knows
        // the orb received the drop and the surface just isn't annotated yet.
        setDropMissFlash(true);
        setTimeout(() => setDropMissFlash(false), 2200);
        x.set(0);
        y.set(0);
        setRestPosition({ x: 0, y: 0 });
        clearMagneticGlow();
      }
      dragStartRef.current = null;
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [x, y, checkMagneticProximity, clearMagneticGlow, inspectElement, restPosition]);

  const visualScale = isDragging ? 1.15 : isHovered ? 1.08 : 1;

  return (
    <>
      {/* ═══ THE ORB — Premium glass optic ═══ */}
      <motion.div
        ref={orbRef}
        data-lens-toggle="true"
        data-testid="lens-orb"
        className="fixed z-[60] select-none touch-none"
        style={{
          bottom: 32,
          left: 32,
          x: springX,
          y: springY,
          width: ORB_SIZE,
          height: ORB_SIZE,
          cursor: isDragging ? 'grabbing' : 'grab',
          transform: `scale(${visualScale})`,
          transition: 'transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
        onPointerDown={handlePointerDown}
        onPointerEnter={() => {
          setIsHovered(true);
          if (!hintDismissed) {
            setHintDismissed(true);
            localStorage.setItem('granullo_lens_hint_seen', 'true');
          }
        }}
        onPointerLeave={() => setIsHovered(false)}
      >
        {/* Premium glass body — iridescent dark gem with scarlet core */}
        <div
          className="absolute inset-0 rounded-full flex items-center justify-center lens-orb-body"
          style={{
            background: 'radial-gradient(circle at 30% 28%, #2A2A2E 0%, #0F0F12 55%, #000000 100%)',
            boxShadow: isDragging
              ? `0 10px 32px rgba(15,23,42,0.28), 0 0 0 1px rgba(255,255,255,0.06), 0 0 24px ${SCARLET}35, inset 0 1px 0 rgba(255,255,255,0.12)`
              : isHovered
                ? `0 6px 22px rgba(15,23,42,0.22), 0 0 0 1px rgba(255,255,255,0.05), 0 0 16px ${SCARLET}20, inset 0 1px 0 rgba(255,255,255,0.1)`
                : `0 4px 14px rgba(15,23,42,0.18), 0 0 0 1px rgba(255,255,255,0.04), 0 0 10px ${SCARLET}12, inset 0 1px 0 rgba(255,255,255,0.08)`,
            transition: 'box-shadow 280ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 250ms ease',
            opacity: focusedAnnotation ? 0.85 : 1,
          }}
        >
          {/* Specular highlight — liquid glass sheen */}
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              top: '10%',
              left: '18%',
              width: '42%',
              height: '28%',
              background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 70%)',
              filter: 'blur(1px)',
            }}
          />

          {/* Reticle icon — precision optic with breathing scarlet core */}
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" style={{ position: 'relative', zIndex: 2 }}>
            <circle cx="12" cy="12" r="7" stroke="#FFFFFF" strokeWidth="1.1" opacity="0.5" />
            <line x1="12" y1="3" x2="12" y2="7" stroke="#FFFFFF" strokeWidth="0.9" opacity="0.4" strokeLinecap="round" />
            <line x1="12" y1="17" x2="12" y2="21" stroke="#FFFFFF" strokeWidth="0.9" opacity="0.4" strokeLinecap="round" />
            <line x1="3" y1="12" x2="7" y2="12" stroke="#FFFFFF" strokeWidth="0.9" opacity="0.4" strokeLinecap="round" />
            <line x1="17" y1="12" x2="21" y2="12" stroke="#FFFFFF" strokeWidth="0.9" opacity="0.4" strokeLinecap="round" />
            <motion.circle
              cx="12"
              cy="12"
              r="2.4"
              fill={SCARLET}
              /* Framer needs the attribute's starting value declared, or the
                 first animation frame writes r="undefined" and the browser
                 logs an SVG attribute error on every mount. */
              initial={{ r: 2.4, opacity: 0.75 }}
              animate={{ opacity: [0.75, 1, 0.75], r: [2.2, 2.6, 2.2] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ filter: `drop-shadow(0 0 3px ${SCARLET})` }}
            />
          </svg>

          {/* Ambient breathing halo — scarlet atmosphere */}
          <motion.div
            className="absolute rounded-full pointer-events-none"
            style={{
              inset: -6,
              border: `1px solid ${SCARLET}`,
              opacity: 0,
            }}
            animate={{
              opacity: sectionPulse ? [0, 0.6, 0] : [0.08, 0.22, 0.08],
              scale: sectionPulse ? [1, 1.55, 1.55] : [1, 1.18, 1],
            }}
            transition={{
              duration: sectionPulse ? 0.8 : 3.2,
              repeat: sectionPulse ? 0 : Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        {/* ═══ PREMIUM TOOLTIP — smooth pill, anchored above orb ═══ */}
        {/* Static positioning wrapper — isolates centering from framer-motion's managed transform,
            and escapes the 48px orb's shrink-to-fit containing-block so tooltip text can't be crushed.
            Anchored to the orb's left edge (expands rightward) because the orb lives at the viewport's
            bottom-left corner and a centered tooltip would clip off-screen. */}
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 10px)',
            left: 0,
            width: 'max-content',
            maxWidth: 320,
            pointerEvents: 'none',
          }}
        >
          <AnimatePresence mode="wait">
            {(() => {
              // Priority: dragging > hover > corridor context > idle
              const pillStyle = {
                display: 'inline-block',
                fontSize: 11,
                fontFamily: '"Inter", "Inter Tight", system-ui, sans-serif',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                padding: '6px 11px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                background: 'rgba(17, 17, 20, 0.92)',
                color: 'rgba(255, 255, 255, 0.92)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18), 0 1px 2px rgba(0, 0, 0, 0.12)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
              };

              const motionProps = {
                initial: { opacity: 0, y: 4, scale: 0.96 },
                animate: { opacity: 1, y: 0, scale: 1 },
                exit: { opacity: 0, y: 4, scale: 0.96 },
                transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
                style: { transformOrigin: 'center bottom' },
              };

              if (isDragging) {
                return (
                  <motion.div key="drag" {...motionProps}>
                    <span style={{ ...pillStyle, background: SCARLET, borderColor: 'rgba(255,255,255,0.12)', boxShadow: `0 4px 14px ${SCARLET}44, 0 0 20px ${SCARLET}33` }}>
                      Drop on any section to inspect it
                    </span>
                  </motion.div>
                );
              }

              if (dropMissFlash) {
                return (
                  <motion.div key="miss" {...motionProps}>
                    <span style={{ ...pillStyle, background: '#1f2937', borderColor: 'rgba(255,255,255,0.1)', whiteSpace: 'normal', maxWidth: 240, borderRadius: 10 }}>
                      Drop on a highlighted card or section — try the Pattern Library, Twin doctrine, or any primitive.
                    </span>
                  </motion.div>
                );
              }

              if (isHovered && !corridorNarrative) {
                return (
                  <motion.div key="hover" {...motionProps}>
                    <span style={pillStyle}>Drag me onto anything for clarity</span>
                  </motion.div>
                );
              }

              if (corridorNarrative) {
                return (
                  <motion.div key="corridor" {...motionProps}>
                    <span style={{ ...pillStyle, whiteSpace: 'normal', maxWidth: 260, borderRadius: 10, textAlign: 'left' }}>
                      {corridorNarrative}
                    </span>
                  </motion.div>
                );
              }

              // Idle state — minimal, breathing
              if (!focusedAnnotation && !hintDismissed) {
                return (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 0.82, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <span style={{ ...pillStyle, fontSize: 10.5, opacity: 0.94 }}>
                      Drag me for clarity
                    </span>
                  </motion.div>
                );
              }

              return null;
            })()}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
}

export default LensOrb;
